package com.islamicmedia.shared.util

private val EMPTY_YEAR_PARENS = Regex("""\s*\(\s*\)\s*$""")

/**
 * Strips a trailing empty-year parenthesis from a display title.
 *
 * Historical DB rows have titles like "Bullfrogs & Lizards ()" because the original
 * format-string check tested year != null instead of year.isNullOrBlank(). Server-side
 * and client-side displayTitle computations are fixed for new data, but old rows still
 * contain the suffix. This helper sanitizes anything coming back from the server before
 * it reaches the UI — defense-in-depth so we never render the empty parens regardless
 * of source.
 *
 * Preserved unchanged: "Title (1985)", "Title (Book)", "Title".
 */
fun String.sanitizeDisplayTitle(): String =
    EMPTY_YEAR_PARENS.replace(this, "")

private val TRAILING_YEAR_PARENS = Regex("""\s*\((?:1[0-9]{3}|2[0-9]{3})\)\s*$""")

/**
 * Drops a trailing "(1999)" / "(2018)" from a display title, for places that address the user
 * about a work rather than identifying it.
 *
 * The year is NOT noise in general — it is what keeps two works with the same name apart, and
 * removing it from the identity is what once let one analysis blend two different films. So this
 * is display-only, and deliberately narrow: the caller decides that a sentence like "To search
 * Bluey, please subscribe" reads better than one with a bracketed date in the middle of it, while
 * everything that searches, caches or reports keeps the full title.
 *
 * Only a four-digit year in 1000-2999 is matched, so "Title (Book)", "Title (Extended Cut)" and
 * "Title (2)" all survive intact.
 */
fun String.withoutTrailingYear(): String =
    TRAILING_YEAR_PARENS.replace(this, "").ifBlank { this }
